import { catchError, defer, first, Observable, of, switchMap, timeout } from 'rxjs';

import { AuthService } from '@tablesready/frontend-auth';
import { AuthRestaurantService } from '@tablesready/frontend-store';
import { AlertsService } from '@tablesready/frontend-alerts-bar';
import { LoggerService } from '@tablesready/frontend-logger';
import { AuthUser } from '@shared/signup/models';

/** Upper bound on hydrating the session at boot; past it we fall back to login. */
export const AUTH_BOOTSTRAP_TIMEOUT_MS = 30000;

export const AUTH_BOOTSTRAP_FAILED_MESSAGE =
  'We could not load your account. Please check your connection and log in again.';

/**
 * APP_INITIALIZER that hydrates the auth stores for a recoverable session before the
 * first route resolves.
 *
 * Must never reject or hang. An APP_INITIALIZER rejection aborts `bootstrapModule`, so
 * there is no router to redirect with and the user gets a blank page. That is exactly
 * what happened when the stored session had expired: every `/v.1/users/*` call 401'd,
 * the interceptor's refresh also 401'd, the interceptor logged out and rethrew, and the
 * rethrow killed bootstrap.
 *
 * Two failure paths, both resolve to `null` so the app boots and `AppCanActivateGuard`
 * routes to `/login` (the alerts outlet replays the error raised here):
 * - session rejected (401): the interceptor already cleared the tokens and raised the
 *   "session expired" alert, nothing more to do;
 * - transient failure (5xx, timeout) with tokens still present: log out explicitly.
 *   Booting into the authenticated layout with empty auth stores is not an option: its
 *   `!user.enabled` / missing `_id` watchers would log out anyway, with a misleading
 *   "account disabled" message, and the plan guards would throw on `user.account`.
 */
export function authAppInitializerFactory(
  authService: AuthService,
  authRestaurantService: AuthRestaurantService,
  alertsService: AlertsService,
  logger: LoggerService,
): () => Observable<AuthUser | null> {
  return (): Observable<AuthUser | null> => defer(() => {
    if (!authService.isAuthenticated()) {
      return of(null);
    }

    return authRestaurantService.load().pipe(
      switchMap(() => authRestaurantService.buildOnce()),
      // `load()` is a forkJoin of five requests with no timeout of their own; a
      // request that never settles would otherwise keep bootstrap (and the blank
      // page) waiting forever.
      timeout({ first: AUTH_BOOTSTRAP_TIMEOUT_MS }),
    );
  }).pipe(
    // Empty hydration is a failure too: do not enter the app with unbuilt auth stores.
    first(),
    catchError((err: unknown) => {
      logger.error(err);

      if (authService.isAuthenticated()) {
        authService.logout();
        alertsService.addError(AUTH_BOOTSTRAP_FAILED_MESSAGE);
      }

      return of(null);
    }),
  );
}
